import { useCallback, useEffect, useRef } from "react"
import {
  ensurePusher,
  subscribeChannel,
  unsubscribeChannel,
} from "../services/pusher"
import {
  SESSION_COLLABORATOR_LEFT_EVENTS,
  SESSION_COLLABORATOR_REACTION_EVENTS,
  SESSION_COLLABORATOR_REACTION_REMOVED_EVENTS,
  SESSION_COLLABORATOR_UPDATED_EVENTS,
  getSessionDetailChannelName,
  matchesSessionRealtime,
  parseSessionCollaboratorPayload,
  parseSessionCollaboratorReactionPayload,
} from "../constants/sessionRealtime"

const bindNamedEvents = (target, names, handler) => {
  if (!target || typeof target.bind !== "function") return
  names.forEach(name => target.bind(name, handler))
}

const unbindNamedEvents = (target, names, handler) => {
  if (!target || typeof target.unbind !== "function") return
  names.forEach(name => target.unbind(name, handler))
}

/**
 * Live session collaborators on public channel session-details.{sessionId}.
 * Updates local member rows / impressions only — does not refetch REST.
 */
const useSessionCollaboratorRealtime = ({
  sessionId,
  enabled = true,
  onUpdated,
  onLeft,
  onReaction,
} = {}) => {
  const callbacksRef = useRef({ onUpdated, onLeft, onReaction })
  callbacksRef.current = { onUpdated, onLeft, onReaction }

  const handleUpdated = useCallback(
    payload => {
      const parsed = parseSessionCollaboratorPayload(payload)
      if (!parsed?.collaborator) return
      if (!matchesSessionRealtime(sessionId, parsed.sessionId)) return
      callbacksRef.current.onUpdated?.(parsed.collaborator)
    },
    [sessionId],
  )

  const handleLeft = useCallback(
    payload => {
      const parsed = parseSessionCollaboratorPayload(payload)
      if (!parsed?.collaborator) return
      if (!matchesSessionRealtime(sessionId, parsed.sessionId)) return
      callbacksRef.current.onLeft?.(parsed.collaborator)
    },
    [sessionId],
  )

  const handleReaction = useCallback(
    payload => {
      const parsed = parseSessionCollaboratorReactionPayload(payload)
      if (!parsed?.collaboratorId) return
      if (!matchesSessionRealtime(sessionId, parsed.sessionId)) return
      callbacksRef.current.onReaction?.(parsed)
    },
    [sessionId],
  )

  const handleReactionRemoved = useCallback(
    payload => {
      const parsed = parseSessionCollaboratorReactionPayload(payload, {
        removed: true,
      })
      if (!parsed?.collaboratorId) return
      if (!matchesSessionRealtime(sessionId, parsed.sessionId)) return
      callbacksRef.current.onReaction?.(parsed)
    },
    [sessionId],
  )

  useEffect(() => {
    if (!enabled || !sessionId) return undefined

    const channelName = getSessionDetailChannelName(sessionId)
    if (!channelName) return undefined

    const pusher = ensurePusher()
    if (!pusher) return undefined

    const channel = subscribeChannel(channelName)
    if (!channel) return undefined

    const unbindAll = () => {
      unbindNamedEvents(
        channel,
        SESSION_COLLABORATOR_UPDATED_EVENTS,
        handleUpdated,
      )
      unbindNamedEvents(channel, SESSION_COLLABORATOR_LEFT_EVENTS, handleLeft)
      unbindNamedEvents(
        channel,
        SESSION_COLLABORATOR_REACTION_EVENTS,
        handleReaction,
      )
      unbindNamedEvents(
        channel,
        SESSION_COLLABORATOR_REACTION_REMOVED_EVENTS,
        handleReactionRemoved,
      )
    }

    const bindAll = () => {
      if (channel && !channel.subscribed) {
        try {
          pusher.subscribe(channelName)
        } catch {
          // ignore resubscribe races
        }
      }
      unbindAll()
      bindNamedEvents(
        channel,
        SESSION_COLLABORATOR_UPDATED_EVENTS,
        handleUpdated,
      )
      bindNamedEvents(channel, SESSION_COLLABORATOR_LEFT_EVENTS, handleLeft)
      bindNamedEvents(
        channel,
        SESSION_COLLABORATOR_REACTION_EVENTS,
        handleReaction,
      )
      bindNamedEvents(
        channel,
        SESSION_COLLABORATOR_REACTION_REMOVED_EVENTS,
        handleReactionRemoved,
      )
    }

    bindAll()
    pusher.connection.bind("connected", bindAll)
    if (typeof channel.bind === "function") {
      channel.bind("pusher:subscription_succeeded", bindAll)
    }

    return () => {
      pusher.connection.unbind("connected", bindAll)
      if (typeof channel.unbind === "function") {
        channel.unbind("pusher:subscription_succeeded", bindAll)
      }
      unbindAll()
      unsubscribeChannel(channelName)
    }
  }, [
    enabled,
    handleLeft,
    handleReaction,
    handleReactionRemoved,
    handleUpdated,
    sessionId,
  ])
}

export default useSessionCollaboratorRealtime
